// GroundingChip — the preview end of grounding, pure. Composes Badge.
//
// .claude/research/design/04: the factual axis rolls up grounding into three honest states,
// each shown as icon + label + tint, colour never alone, NO numeric confidence.
// This chip is the glanceable preview (it rides along on a proposal in the
// ReviewControl and on an element's gutter); GroundingEvidence is the paired
// inspection end, and both speak this exact three-state vocabulary so accepting
// a proposal carries its evidence in unchanged.
//
// The tone/icon/label are chosen per state via a match over the `GroundingState`
// discriminant — a new state breaks the Record until its row is written. The
// mapping onto Badge tones is deliberate: Grounded → success, Weak → warning,
// Ungrounded → neutral (a call to link a source, NOT an alarm — .claude/research/design/04
// "the tone matters"). The label text always carries the meaning; the icon is
// the required redundant channel Badge demands for a status tone.
//
// Pure/props-driven: the only input is the state. No interactivity here — the
// chip is display; the composing context (GroundingEvidence) wraps it in a
// trigger when inspection is offered.

import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { Badge } from "../../Badge/Badge.js";
import { Icon } from "../../Icon/Icon.js";
import type { GroundingState } from "../types.js";

export type GroundingChipProps = {
    readonly state: GroundingState;
};

const groundedGlyph = <Icon name="check" size="xs" />;

const weakGlyph = <Icon name="warning" size="xs" />;

const ungroundedGlyph = <Icon name="unlink" size="xs" />;

// The chip's rendered atoms per state. Not exported — GroundingEvidence reads
// the same state and re-derives its own presentation from the same source.
export const GroundingChip = ({ state }: GroundingChipProps): ReactNode => {
    const { t } = useLingui();
    switch (state.type) {
        case "grounded":
            return (
                <Badge tone="success" icon={groundedGlyph}>
                    {t`Grounded`}
                </Badge>
            );
        case "weak":
            return (
                <Badge tone="warning" icon={weakGlyph}>
                    {t`Weak`}
                </Badge>
            );
        case "ungrounded":
            return (
                <Badge tone="neutral" icon={ungroundedGlyph}>
                    {t`Ungrounded`}
                </Badge>
            );
        case "unsupported":
            return (
                <Badge tone="warning" icon={ungroundedGlyph}>
                    {t`Unsupported`}
                </Badge>
            );
        default: {
            // Exhaustiveness: a new GroundingState variant fails to compile here
            // (no throw — the value is `never`, assignable to ReactNode).
            const exhaustive: never = state;
            return exhaustive;
        }
    }
};
